// components/ImportStudentsModal.jsx
import React, { useMemo, useState, useEffect } from 'react';
import * as XLSX from 'xlsx';

// Canonical template headers (required + common optional)
const TEMPLATE_HEADERS = [
  'admission_no',      // REQUIRED
  'firstname',         // REQUIRED
  'lastname',          // REQUIRED
  'gender',            // REQUIRED: male | female
  'classroom_name',    // optional unless you toggle "Require classroom assignment"

  // Optional, stored as null if omitted:
  'middlename',
  'date_of_birth',
  'admission_date',
  'student_email',
  'student_phone',
  'parent_name',
  'parent_phone',
  'parent_email',
  'address',
  'status',       // (active, inactive, confirmed, suspended, expelled, graduated, transfered)
  'post',
  'class_post',
];

const REQUIRED_FIELDS = ['admission_no', 'firstname', 'lastname', 'gender'];

const FRIENDLY_HEADERS_MAP = new Map([
  // admission_no
  ['admission no', 'admission_no'],
  ['admission_no', 'admission_no'],
  ['adm no', 'admission_no'],
  ['adm_no', 'admission_no'],
  // firstname
  ['first name', 'firstname'],
  ['first_name', 'firstname'],
  ['firstname', 'firstname'],
  // lastname
  ['last name', 'lastname'],
  ['last_name', 'lastname'],
  ['surname', 'lastname'],
  ['lastname', 'lastname'],
  // middlename (optional)
  ['middle name', 'middlename'],
  ['middle_name', 'middlename'],
  ['middlename', 'middlename'],
  // gender
  ['gender', 'gender'],
  // classroom_name
  ['class', 'classroom_name'],
  ['class name', 'classroom_name'],
  ['class_name', 'classroom_name'],
  ['classroom', 'classroom_name'],
  ['classroom name', 'classroom_name'],
  ['classroom_name', 'classroom_name'],
  // optional common fields
  ['date of birth', 'date_of_birth'],
  ['date_of_birth', 'date_of_birth'],
  ['dob', 'date_of_birth'],
  ['admission date', 'admission_date'],
  ['admission_date', 'admission_date'],
  ['student email', 'student_email'],
  ['student_email', 'student_email'],
  ['student phone', 'student_phone'],
  ['student_phone', 'student_phone'],
  ['parent name', 'parent_name'],
  ['parent_name', 'parent_name'],
  ['parent phone', 'parent_phone'],
  ['parent_phone', 'parent_phone'],
  ['parent email', 'parent_email'],
  ['parent_email', 'parent_email'],
  ['address', 'address'],
  ['status', 'status'],
  ['post', 'post'],
  ['class post', 'class_post'],
  ['class_post', 'class_post'],
]);

function canonicalizeHeader(h) {
  if (!h) return '';
  const raw = String(h).trim().toLowerCase().replace(/\s+/g, ' ');
  if (FRIENDLY_HEADERS_MAP.has(raw)) return FRIENDLY_HEADERS_MAP.get(raw);
  return raw.replace(/[^\w]+/g, '_'); // fallback: "Some Header" -> "some_header"
}

function sanitizeValue(v) {
  if (v === undefined || v === null) return '';
  if (typeof v === 'string') return v.trim();
  return v;
}

function toCSV(rows) {
  if (!rows?.length) return '';
  const headers = Array.from(
    rows.reduce((set, r) => {
      Object.keys(r || {}).forEach((k) => set.add(k));
      return set;
    }, new Set())
  );
  const escape = (val) => {
    const s = val == null ? '' : String(val);
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const lines = [
    headers.map(escape).join(','),
    ...rows.map((r) => headers.map((h) => escape(r[h] ?? '')).join(',')),
  ];
  return lines.join('\n');
}

function downloadTemplateXLSX() {
  // First row = headers, second row = example (you can delete it after download)
  const aoa = [
    TEMPLATE_HEADERS,
    // Example row (safe defaults; helps users see expected values)
    ['ADM-001', 'Ada', 'Okafor', 'female', 'JSS1A', '', '', '', 'ada@example.com', '08012345678', '', '', '', 'Plot 1, Main St', 'active', '', ''],
  ];

  const ws = XLSX.utils.aoa_to_sheet(aoa);

  // Freeze header row + set column widths
  ws['!freeze'] = { xSplit: 0, ySplit: 1 };
  ws['!cols'] = TEMPLATE_HEADERS.map(h => ({ wch: Math.max(14, h.length + 2) }));

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Students');

  XLSX.writeFile(wb, 'students_template.xlsx');
}

export default function ImportStudentsModal({
  isOpen,
  onClose,
  onImported,            // callback to refresh list after success
  apiBaseUrl,
  authToken,            // pass your token or rely on localStorage
}) {
  const [fileName, setFileName] = useState('');
  const [rows, setRows] = useState([]);               // [{...}]
  const [requireClassroom, setRequireClassroom] = useState(false);
  const [parseError, setParseError] = useState('');
  const [busy, setBusy] = useState(false);

  // Backend report
  const [importSummary, setImportSummary] = useState(null); // { inserted, updated, skipped, errors: [{index, admission_no, error}] }
  const [failedRows, setFailedRows] = useState([]);

  // Reset when closed
  useEffect(() => {
    if (!isOpen) {
      setFileName('');
      setRows([]);
      setRequireClassroom(false);
      setParseError('');
      setBusy(false);
      setImportSummary(null);
      setFailedRows([]);
    }
  }, [isOpen]);

  const allColumns = useMemo(() => {
    // Union of keys across rows, plus required, plus classroom_name
    const set = new Set(['classroom_name', ...REQUIRED_FIELDS]);
    rows.forEach((r) => Object.keys(r || {}).forEach((k) => set.add(k)));
    return Array.from(set);
  }, [rows]);

  const { invalidCells, isValid } = useMemo(() => {
    const invalid = new Map(); // rowIndex -> Set(fields)
    let valid = true;

    const requiredNow = requireClassroom
      ? [...REQUIRED_FIELDS, 'classroom_name']
      : [...REQUIRED_FIELDS];

    rows.forEach((r, idx) => {
      requiredNow.forEach((f) => {
        const val = r?.[f];
        const missing = val === undefined || val === null || String(val).trim() === '';
        if (missing) {
          valid = false;
          if (!invalid.has(idx)) invalid.set(idx, new Set());
          invalid.get(idx).add(f);
        }
      });
      // normalize gender values in preview (optional; helps user)
      if (r?.gender) {
        const g = String(r.gender).toLowerCase();
        if (!['male', 'female', 'm', 'f'].includes(g)) {
          valid = false;
          if (!invalid.has(idx)) invalid.set(idx, new Set());
          invalid.get(idx).add('gender');
        }
      }
    });
    return { invalidCells: invalid, isValid: valid && rows.length > 0 };
  }, [rows, requireClassroom]);

  const handleFile = async (file) => {
    setFileName(file?.name || '');
    setParseError('');
    setImportSummary(null);
    setFailedRows([]);

    try {
      const data = await file.arrayBuffer();
      const wb = XLSX.read(data, { type: 'array' });
      const sheet = wb.Sheets[wb.SheetNames[0]];
      let json = XLSX.utils.sheet_to_json(sheet, { defval: '' });

      // Canonicalize headers & trim values
      json = json.map((row) => {
        const next = {};
        Object.entries(row || {}).forEach(([k, v]) => {
          const key = canonicalizeHeader(k);
          next[key] = sanitizeValue(v);
        });

        // ensure these fields exist even if missing in sheet
        REQUIRED_FIELDS.concat(['classroom_name']).forEach((f) => {
          if (!(f in next)) next[f] = '';
        });

        return next;
      });

      setRows(json);
    } catch (err) {
      console.error(err);
      setParseError('Could not read the file. Ensure it is a valid Excel/CSV.');
      setRows([]);
    }
  };

  const handleCellEdit = (rowIdx, col, value) => {
    setRows((prev) => {
      const next = [...prev];
      next[rowIdx] = { ...next[rowIdx], [col]: value };
      return next;
    });
  };

  const handleImport = async () => {
    if (!isValid || rows.length === 0) return;
    setBusy(true);
    setImportSummary(null);
    setFailedRows([]);

    try {
      const token = authToken || localStorage.getItem('authToken');
      const res = await fetch(`${apiBaseUrl}/api/web/admin/students/import`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          students: rows,
          requireClassroom,
        }),
      });

      const out = await res.json();
      if (!res.ok || !out?.success) {
        throw new Error(out?.message || 'Import failed');
      }

      setImportSummary(out.summary || null);

      // Build failed rows CSV if any
      if (Array.isArray(out.summary?.errors) && out.summary.errors.length > 0) {
        const failing = out.summary.errors
          .map((e) => rows[e.index])
          .filter(Boolean);
        setFailedRows(failing);
      }

      // If everything ok and no failures, auto refresh main list
      if ((out.summary?.errors || []).length === 0) {
        if (typeof onImported === 'function') onImported();
      }
    } catch (err) {
      console.error(err);
      setImportSummary({
        inserted: 0,
        updated: 0,
        skipped: rows.length,
        errors: [{ index: 0, admission_no: '', error: err.message }],
      });
    } finally {
      setBusy(false);
    }
  };

  const downloadFailed = () => {
    if (!failedRows.length) return;
    const csv = toCSV(failedRows);
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'failed_rows.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="w-full max-w-5xl bg-white dark:bg-gray-900 rounded-xl shadow-lg overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
            Import Students
          </h3>
          <button
            onClick={onClose}
            className="px-3 py-1 rounded-lg text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800"
          >
            Close
          </button>
        </div>

        {/* Controls */}
        <div className="p-4 space-y-4">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
            <div className="flex items-center gap-3">
              <input
                type="file"
                accept=".xlsx,.xls,.csv"
                onChange={(e) => e.target.files?.[0] && handleFile(e.target.files[0])}
                className="block w-full text-sm text-gray-900 dark:text-gray-200 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100 dark:file:bg-gray-800 dark:file:text-gray-200"
              />
              {fileName && (
                <span className="text-sm text-gray-500 dark:text-gray-400">{fileName}</span>
              )}
            </div>

            <div className="flex items-center gap-3">
              <label className="inline-flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                <input
                  type="checkbox"
                  checked={requireClassroom}
                  onChange={(e) => setRequireClassroom(e.target.checked)}
                  className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                />
                Require classroom assignment for each row
              </label>

              <button
                type="button"
                onClick={downloadTemplateXLSX}
                className="px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800 text-sm"
              >
                Download .xlsx template
              </button>
            </div>
          </div>

          {parseError && (
            <div className="p-3 rounded-lg bg-red-50 text-red-700 dark:bg-red-900/20 dark:text-red-300">
              {parseError}
            </div>
          )}

          {/* Tips */}
          <div className="text-xs text-gray-600 dark:text-gray-400">
            Required columns: <span className="font-semibold">admission_no, firstname, lastname, gender</span>.{' '}
            You can optionally include <span className="font-semibold">classroom_name</span> per row.
          </div>
        </div>

        {/* Preview */}
        <div className="px-4 pb-4">
          <div className="rounded-lg border border-gray-200 dark:border-gray-700 overflow-auto max-h-[55vh]">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50 dark:bg-gray-800 sticky top-0 z-10">
                <tr>
                  {allColumns.map((col) => {
                    const required =
                      REQUIRED_FIELDS.includes(col) ||
                      (requireClassroom && col === 'classroom_name');
                    return (
                      <th
                        key={col}
                        className="px-3 py-2 text-left font-semibold text-gray-700 dark:text-gray-200 whitespace-nowrap"
                      >
                        {col}
                        {required && <span className="text-red-500"> *</span>}
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {rows.length === 0 ? (
                  <tr>
                    <td
                      className="px-4 py-6 text-center text-gray-500 dark:text-gray-400"
                      colSpan={allColumns.length || 1}
                    >
                      Upload an Excel/CSV file to preview rows here.
                    </td>
                  </tr>
                ) : (
                  rows.map((r, idx) => (
                    <tr key={idx} className="bg-white dark:bg-gray-900">
                      {allColumns.map((col) => {
                        const val = r[col] ?? '';
                        const isRequiredMissing =
                          invalidCells.get(idx)?.has(col) ?? false;
                        return (
                          <td key={col} className="px-3 py-2 whitespace-nowrap">
                            <input
                              value={val}
                              onChange={(e) => handleCellEdit(idx, col, e.target.value)}
                              className={`w-full px-2 py-1 rounded-md border text-gray-900 dark:text-gray-100 dark:bg-gray-800 ${
                                isRequiredMissing
                                  ? 'border-red-500 ring-1 ring-red-400'
                                  : 'border-gray-300 dark:border-gray-700'
                              }`}
                              placeholder={col}
                            />
                          </td>
                        );
                      })}
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Footer: actions + validation state */}
          <div className="mt-4 flex flex-col md:flex-row md:items-center md:justify-between gap-3">
            <div className="text-sm">
              <span
                className={`inline-block px-2 py-1 rounded ${
                  isValid && rows.length
                    ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-200'
                    : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-200'
                }`}
              >
                {rows.length
                  ? isValid
                    ? 'All rows valid'
                    : 'Fix highlighted fields before importing'
                  : 'No rows loaded'}
              </span>
              {rows.length > 0 && (
                <span className="ml-3 text-gray-500 dark:text-gray-400">
                  {rows.length} row(s)
                </span>
              )}
            </div>

            <div className="flex items-center gap-2">
              <button
                onClick={() => {
                  setRows([]);
                  setFileName('');
                  setImportSummary(null);
                  setFailedRows([]);
                }}
                className="px-4 py-2 border rounded-lg text-gray-700 dark:text-gray-300 border-gray-300 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800"
              >
                Clear
              </button>
              <button
                onClick={handleImport}
                disabled={!isValid || rows.length === 0 || busy}
                className={`px-4 py-2 rounded-lg text-white ${
                  !isValid || rows.length === 0 || busy
                    ? 'bg-blue-400 cursor-not-allowed'
                    : 'bg-blue-600 hover:bg-blue-700'
                }`}
              >
                {busy ? 'Importing…' : 'Import'}
              </button>
            </div>
          </div>

          {/* Backend summary */}
          {importSummary && (
            <div className="mt-4 space-y-3">
              <div className="rounded-lg border border-gray-200 dark:border-gray-700 p-3">
                <div className="font-semibold text-gray-900 dark:text-gray-100 mb-2">
                  Import Summary
                </div>
                <div className="text-sm text-gray-700 dark:text-gray-300">
                  Inserted: <b>{importSummary.inserted ?? 0}</b> • Updated: <b>{importSummary.updated ?? 0}</b> • Skipped: <b>{importSummary.skipped ?? 0}</b>
                </div>
              </div>

              {Array.isArray(importSummary.errors) && importSummary.errors.length > 0 && (
                <div className="rounded-lg border border-red-200 dark:border-red-700 p-3 bg-red-50 dark:bg-red-900/20">
                  <div className="font-semibold text-red-700 dark:text-red-300 mb-2">
                    Issues Found ({importSummary.errors.length})
                  </div>
                  <div className="max-h-40 overflow-auto text-sm">
                    <ul className="list-disc pl-5 text-red-700 dark:text-red-300">
                      {importSummary.errors.map((e, i) => (
                        <li key={i}>
                          Row {e.index + 2}{' '}
                          {e.admission_no ? `(admission_no: ${e.admission_no}) ` : ''}— {e.error}
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div className="mt-3 flex gap-2">
                    <button
                      onClick={downloadFailed}
                      className="px-3 py-1 rounded bg-red-600 text-white hover:bg-red-700"
                    >
                      Download Failed Rows (CSV)
                    </button>
                    <button
                      onClick={() => {
                        if (typeof onImported === 'function') onImported();
                      }}
                      className="px-3 py-1 rounded border border-gray-300 dark:border-gray-700 text-gray-800 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800"
                    >
                      Refresh List
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
